import type { GameConfig } from './GameState.ts';

/** Mutable bird body. Kept separate from GameState so snapshots stay frozen. */
export interface Bird {
  y: number;
  vy: number;
}

/**
 * One fixed-timestep integration step (semi-implicit Euler with linear drag).
 *
 *     v <- v + (gravity - drag * v) * dt
 *     y <- y + v * dt
 *
 * Semi-implicit Euler rather than RK4 because it is stable, cheap, and -- more
 * importantly -- because `predict()` below solves this exact recurrence in
 * closed form. The bot's forecast and the engine's integration are the same
 * arithmetic, not two approximations of the same differential equation.
 *
 * dt is ALWAYS the fixed step; never a frame delta.
 */
export function integrate(bird: Bird, config: GameConfig, dt: number, flap: boolean): void {
  if (flap) {
    bird.vy = config.flapImpulse;
  }
  bird.vy += (config.gravity - config.drag * bird.vy) * dt;
  bird.vy = Math.min(bird.vy, config.maxFallSpeed);
  bird.y += bird.vy * dt;
}

/** Steady sink rate the bird settles at, px/s. Infinite when there is no drag. */
export function terminalVelocity(config: GameConfig): number {
  if (config.drag <= 0) return config.maxFallSpeed;
  return Math.min(config.maxFallSpeed, config.gravity / config.drag);
}

/**
 * Where the bird will be `seconds` from now if nothing flaps in between.
 *
 * This is the core of latency compensation: the bot asks "where will the bird
 * be by the time a stimulated arm has actually pressed the button?" rather than
 * "where is the bird now?".
 *
 * Solved in closed form for the *discrete* recurrence in `integrate`, not for
 * the underlying ODE, so the forecast does not slowly drift away from the
 * simulation over a long horizon. Writing
 *
 *     r  = 1 - drag * dt          (per-step velocity decay)
 *     vt = gravity / drag         (steady sink rate)
 *
 * the velocity after n steps is `vt + (v0 - vt) * r^n`, and summing that series
 * gives the displacement below. With `drag = 0` both reduce to the familiar
 * `v0 + g t` and `v0 t + ½ g t²`, which is the branch taken first.
 */
export function predict(
  y: number,
  vy: number,
  config: GameConfig,
  seconds: number,
): { y: number; vy: number } {
  if (seconds <= 0) return { y, vy };

  const g = config.gravity;
  const k = config.drag;
  const vMax = config.maxFallSpeed;

  if (k <= 0) {
    // Undamped: constant acceleration up to the hard speed cap.
    const tToTerminal = vy >= vMax ? 0 : (vMax - vy) / g;
    if (!Number.isFinite(tToTerminal) || seconds <= tToTerminal) {
      return { y: y + vy * seconds + 0.5 * g * seconds * seconds, vy: vy + g * seconds };
    }
    const yAtTerminal = y + vy * tToTerminal + 0.5 * g * tToTerminal * tToTerminal;
    return { y: yAtTerminal + vMax * (seconds - tToTerminal), vy: vMax };
  }

  const dt = 1 / config.simHz;
  const r = 1 - k * dt;
  const vTerm = Math.min(vMax, g / k);

  // A drag coefficient stiff enough to make r <= 0 would oscillate in the
  // integrator too; treat it as "settles immediately" rather than producing
  // nonsense from log(negative).
  if (r <= 0) return { y: y + vTerm * seconds, vy: vTerm };

  const steps = seconds / dt;
  const decay = Math.pow(r, steps);
  const lnr = Math.log(r);
  const v = vTerm + (vy - vTerm) * decay;
  const dy = vTerm * seconds + ((vy - vTerm) * (decay - 1) * dt) / lnr;
  return { y: y + dy, vy: Math.min(v, vMax) };
}

/**
 * How far the bird rises after a flap before gravity wins, px.
 * Used by the bot's roof guard.
 *
 * With drag and low gravity the bird can coast a long way upward, so this is no
 * longer just `v² / 2g`; it is the displacement up to the moment the damped
 * velocity crosses zero. With gravity at zero it never does cross zero, and the
 * bird's total rise is the whole (finite) integral of a decaying velocity.
 */
export function flapApexRise(config: GameConfig): number {
  const v0 = config.flapImpulse;
  const g = config.gravity;
  const k = config.drag;

  if (k <= 0) {
    if (g <= 0) return Number.POSITIVE_INFINITY;
    return (v0 * v0) / (2 * g);
  }

  const dt = 1 / config.simHz;
  const r = 1 - k * dt;
  if (r <= 0) return 0;
  const lnr = Math.log(r);
  const vTerm = Math.min(config.maxFallSpeed, g / k);

  // Zero gravity: velocity decays to zero and the bird simply stops, higher up.
  if (vTerm <= 0) return Math.abs((v0 * dt) / lnr);

  // Time at which the damped velocity reaches zero, then the rise over it.
  const ratio = vTerm / (vTerm - v0);
  if (ratio <= 0 || ratio >= 1) return 0;
  const tApex = (dt * Math.log(ratio)) / lnr;
  return Math.abs(predict(0, v0, config, tApex).y);
}

/** Circle (bird) vs axis-aligned rectangle (pipe half) overlap test. */
export function circleRectOverlap(
  cx: number,
  cy: number,
  r: number,
  rx: number,
  ry: number,
  rw: number,
  rh: number,
): boolean {
  const nearestX = Math.max(rx, Math.min(cx, rx + rw));
  const nearestY = Math.max(ry, Math.min(cy, ry + rh));
  const dx = cx - nearestX;
  const dy = cy - nearestY;
  return dx * dx + dy * dy < r * r;
}

/**
 * Ballistic prediction that also accounts for flaps already "in flight".
 *
 * In stimulation mode the bot issues intent and then waits ~80 ms for a human
 * arm to respond. During that window the bird is still falling and the bot must
 * not spam further requests -- it needs to know "an impulse is already on its
 * way, here is where I will actually be once it lands".
 *
 * @param flapTimesSec offsets from now (seconds) at which pending impulses are
 *   expected to be applied. Values outside [0, horizonSec] are ignored. An
 *   offset of exactly 0 means "lands on this tick" and is applied first.
 */
export function predictWithFlaps(
  y: number,
  vy: number,
  config: GameConfig,
  horizonSec: number,
  flapTimesSec: readonly number[] = [],
): { y: number; vy: number } {
  const times = flapTimesSec
    .filter((t) => t >= 0 && t <= horizonSec)
    .slice()
    .sort((a, b) => a - b);

  let curY = y;
  let curVy = vy;
  let t = 0;
  for (const flapAt of times) {
    const seg = predict(curY, curVy, config, flapAt - t);
    curY = seg.y;
    curVy = config.flapImpulse;
    t = flapAt;
  }
  const tail = predict(curY, curVy, config, horizonSec - t);
  return tail;
}
